import { getGenders, getRaces, getEthnicities, getRelationships } from './clientsService';
import { BREAK_LINE_ID, HEADER_ID, PARAGRAPH_ID } from '../constants/fieldTypes';
import { getClientForm } from './formsService';
import { searchPrograms } from './programsService';
import { getOrganization } from './organizationService';
import Organization from '../models/serviceModels/Organization';
import {
    getEthnicityFieldVisible,
    getGenderOptionsForOrganization,
    getRaceOptionsForOrganization,
    isDemographicVisible,
    splitRaceSelectionsForOrganization,
} from '../utils/demographicsSchema';

export interface ReferenceData {
    genders: Array<{ id: number; name: string }>;
    races: Array<{ id: number; name: string }>;
    clientform: Array<{ 
        id: string; 
        name: string; 
        label: string;
        fieldTypeId: number;
        options?: Array<{ id: string; label: string; value: string }>;
        formId?: string;
        versionId?: string;
    }>;
    ethnicities: Array<{ id: number; name: string }>;
    relationships: Array<{ id: number; name: string }>;
    programs: Array<{ id: string; name: string }>;
    organization: Organization;
    genderOptions: Array<{ label: string; value: string }>;
    ethnicityOptions: Array<{ label: string; value: string }>;
    raceOptions: Array<{ label: string; value: string }>;
}

let cachedReferenceData: ReferenceData | null = null;

export const isMappableAdditionalInfoField = (field: { name: string | null; fieldTypeId: number }) => {
    return field.name !== null
        && field.fieldTypeId !== HEADER_ID
        && field.fieldTypeId !== BREAK_LINE_ID
        && field.fieldTypeId !== PARAGRAPH_ID;
};

export const fetchReferenceData = async (options: { forceRefresh?: boolean } = {}): Promise<ReferenceData> => {
    if (cachedReferenceData && !options.forceRefresh) {
        return cachedReferenceData;
    }

    try {
        const [genders, races, clientform, ethnicities, relationships, programsData, organization] = await Promise.all([
            getGenders(),
            getRaces(),
            getClientForm(),
            getEthnicities(),
            getRelationships(),
            searchPrograms({ query: '', skip: 0, take: 1000, includeDeactivated: false }),
            getOrganization(),
        ]);

        const genderOptions = isDemographicVisible(organization, 'gender')
            ? getGenderOptionsForOrganization(genders, organization)
            : [];
        const ethnicityOptions = getEthnicityFieldVisible(organization)
            ? ethnicities.map(ethnicity => ({ label: ethnicity.name, value: ethnicity.id.toString() }))
            : [];
        const raceOptions = isDemographicVisible(organization, 'race')
            ? getRaceOptionsForOrganization(races, ethnicities, organization)
            : [];

        cachedReferenceData = {
            genders: genders.map(g => ({ id: g.id, name: g.name })),
            races: races.map(r => ({ id: r.id, name: r.name })),
            clientform: clientform ? clientform.fields.filter(isMappableAdditionalInfoField).map(r => ({
                id: r.id, 
                name: r.name, 
                label: r.label,
                fieldTypeId: r.fieldTypeId,
                options: r.options?.map(z => ({ id: z.id, label: z.label, value: z.value })),
                formId: clientform.id,
                versionId: clientform.versionId
            })) : [],
            ethnicities: ethnicities.map(e => ({ id: e.id, name: e.name })),
            relationships: relationships.map(r => ({ id: r.id, name: r.name })),
            programs: programsData.data.map(p => ({ id: p.id, name: p.name })),
            organization,
            genderOptions,
            ethnicityOptions,
            raceOptions,
        };

        return cachedReferenceData;
    } catch (error) {
        console.error('Error fetching reference data:', error);
        throw new Error('Failed to load reference data for import');
    }
};

export const clearReferenceDataCache = () => {
    cachedReferenceData = null;
};

export interface FindByNameOptions {
    /** Allow substring matching (item name contained within input or vice versa).
     *  Safe for races/ethnicities, risky for short gender labels. Default: false */
    allowSubstring?: boolean;
    /** Extra synonym map to merge with the built-in one */
    extraSynonyms?: Record<string, string[]>;
}

export const findIdByName = (
    name: string | undefined | null,
    referenceArray: Array<{ id: number; name: string }>,
    options: FindByNameOptions = {}
): number | undefined => {
    if (!name || name.trim() === '') return undefined;

    const { allowSubstring = false } = options;
    const normalizedInput = name.toLowerCase().trim();

    const synonymMappings: Record<string, string[]> = {
        // Gender
        'man':              ['m', 'male', 'man', 'boy'],
        'woman':            ['f', 'female', 'woman', 'girl', 'lady'],
        'non-binary':       ['nb', 'nonbinary', 'non binary', 'enby', 'non-binary'],
        'prefer not to say':['prefer not to say', 'no answer', 'decline', 'declined', 'unknown'],

        // Relationship
        'spouse':           ['wife', 'husband', 'partner', 'spouse'],
        'child':            ['son', 'daughter', 'child', 'kid'],
        'parent':           ['father', 'mother', 'dad', 'mom', 'parent'],
        'sibling':          ['brother', 'sister', 'sibling'],
        'grandparent':      ['grandfather', 'grandmother', 'grandpa', 'grandma', 'grandparent'],
        'grandchild':       ['grandson', 'granddaughter', 'grandchild'],

        // Race / Ethnicity
        'black or african american': ['black', 'african american', 'african-american'],
        'white or caucasian':        ['white', 'caucasian'],
        'hispanic or latino':        ['hispanic', 'latino', 'latina', 'latinx'],
        'asian':                     ['asian', 'east asian', 'south asian', 'southeast asian'],
        'american indian or alaska native': ['native american', 'american indian', 'alaska native', 'indigenous'],
        'native hawaiian or other pacific islander': ['pacific islander', 'hawaiian', 'native hawaiian'],
        'middle eastern or north african': ['middle eastern', 'north african', 'mena', 'arab'],
        ...(options.extraSynonyms ?? {}),
    };

    // 1. Synonym mapping — always exact equality to prevent cross-matching
    for (const [targetName, synonyms] of Object.entries(synonymMappings)) {
        if (synonyms.some(syn => normalizedInput === syn)) {
            const synonymMatch = referenceArray.find(
                item => item.name.toLowerCase() === targetName
            );
            if (synonymMatch) return synonymMatch.id;
        }
    }

    // 2. Exact match
    const exactMatch = referenceArray.find(
        item => item.name.toLowerCase() === normalizedInput
    );
    if (exactMatch) return exactMatch.id;

    // 3. Special-character-cleaned exact match  (e.g. "non binary" → "nonbinary")
    const cleanedInput = normalizedInput.replace(/[^a-z0-9\s]/g, '').trim();
    const cleanExactMatch = referenceArray.find(
        item => item.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim() === cleanedInput
    );
    if (cleanExactMatch) return cleanExactMatch.id;

    // 4. Substring match — only when explicitly enabled
    if (allowSubstring) {
        // 4a. Whole-word boundary match (safer, preferred)
        const wordBoundaryMatch = referenceArray.find(item => {
            const itemName = item.name.toLowerCase();
            const escapedItemName = itemName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const escapedInput    = normalizedInput.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            return new RegExp(`\\b${escapedItemName}\\b`).test(normalizedInput) ||
                   new RegExp(`\\b${escapedInput}\\b`).test(itemName);
        });
        if (wordBoundaryMatch) return wordBoundaryMatch.id;

        // 4b. Plain substring fallback (broadest net)
        const substringMatch = referenceArray.find(item => {
            const itemName = item.name.toLowerCase();
            return itemName.includes(normalizedInput) || normalizedInput.includes(itemName);
        });
        if (substringMatch) return substringMatch.id;
    }

    // 5. Single-character abbreviation match
    if (normalizedInput.length === 1) {
        for (const [targetName, synonyms] of Object.entries(synonymMappings)) {
            if (synonyms.includes(normalizedInput)) {
                const synonymMatch = referenceArray.find(
                    item => item.name.toLowerCase() === targetName
                );
                if (synonymMatch) return synonymMatch.id;
            }
        }

        const abbrevMatch = referenceArray.find(
            item => item.name.toLowerCase().startsWith(normalizedInput)
        );
        if (abbrevMatch) return abbrevMatch.id;
    }

    return undefined;
};

export const parseRaceValues = (
    raceString: string | undefined | null,
    races: Array<{ id: number; name: string }>
): number[] => {
    if (!raceString || raceString.trim() === '') return [];

    // Use pipe (|) as primary delimiter, with fallback to legacy delimiters
    const parts = raceString
        .split(/\s*(?:\||;|&|\band\/or\b|\band\b|\bor\b)\s*/i)
        .map(s => s.trim())
        .filter(Boolean);

    const raceIds: number[] = [];

    parts.forEach(part => {
        // ✅ substring matching enabled only for races
        const id = findIdByName(part, races, { allowSubstring: true });
        if (id !== undefined && !raceIds.includes(id)) {
            raceIds.push(id);
        }
    });

    return raceIds;
};

export const findOptionValueByName = (
    name: string | undefined | null,
    options: Array<{ label: string; value: string }>,
    findOptions: FindByNameOptions = {}
): string | undefined => {
    if (!name || name.trim() === '') return undefined;

    const optionReferences = options.map((option, index) => ({
        id: index,
        name: option.label
    }));
    const matchIndex = findIdByName(name, optionReferences, findOptions);
    if (matchIndex !== undefined) {
        return options[matchIndex]?.value;
    }

    const normalizedInput = name.toLowerCase().trim();
    const directValueMatch = options.find(option => option.value.toLowerCase() === normalizedInput);
    if (directValueMatch) {
        return directValueMatch.value;
    }

    const isBinaryInput = ['m', 'male', 'man', 'boy', 'f', 'female', 'woman', 'girl', 'lady'].includes(normalizedInput);
    const isKnownOtherGenderInput = [
        'other',
        'non-binary',
        'nonbinary',
        'non binary',
        'nb',
        'enby',
        'some other gender identity'
    ].includes(normalizedInput);
    if (!isBinaryInput && isKnownOtherGenderInput) {
        return options.find(option => option.label.toLowerCase() === 'other')?.value;
    }

    return undefined;
};

export const parseRaceOptionValues = (
    raceString: string | undefined | null,
    referenceData: ReferenceData
) => {
    if (!raceString || raceString.trim() === '') {
        return { ethnicityId: undefined, raceIds: [] };
    }

    const selections = raceString
        .split(/\s*(?:\||;|&|\band\/or\b|\band\b|\bor\b|,)\s*/i)
        .map(s => s.trim())
        .filter(Boolean)
        .map(part => {
            const value = findOptionValueByName(part, referenceData.raceOptions, { allowSubstring: true });
            const option = value
                ? referenceData.raceOptions.find(raceOption => raceOption.value === value)
                : undefined;

            return option;
        })
        .filter((option): option is { label: string; value: string } => option !== undefined);

    return splitRaceSelectionsForOrganization(selections, referenceData.organization, referenceData.races);
};
